/* import __COLOCATED_TEMPLATE__ from './slack-channel-selector.hbs'; */
/* RESPONSIBLE TEAM: team-omni */
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import { get } from 'embercom/lib/ajax';
import type Session from 'embercom/services/session';
import { type InterfaceIconName } from '@intercom/pulse/lib/interface-icons';

interface SlackChannel {
  id: string;
  channel_name: string;
  channel_type: string;
  slack_team_name: string;
}

interface ChannelOption {
  text: string;
  value: string;
  icon?: InterfaceIconName;
}

interface GroupedChannelOption {
  heading: string;
  items: ChannelOption[];
}

interface Args {
  selectedChannelId: string | null;
  onChannelChange: (channelId: string | null) => void;
  isDisabled?: boolean;
}

interface Signature {
  Args: Args;
  Element: HTMLDivElement;
}

export default class SlackChannelSelector extends Component<Signature> {
  @service declare session: Session;

  @tracked slackChannels: SlackChannel[] = [];
  @tracked slackChannelsError: string | null = null;

  constructor(owner: unknown, args: Args) {
    super(owner, args);
    if (this.session.workspace.hasSlackIntegration) {
      taskFor(this.fetchSlackChannels).perform();
    }
  }

  get isLoading(): boolean {
    return taskFor(this.fetchSlackChannels).isRunning;
  }

  @task({ drop: true })
  *fetchSlackChannels(): TaskGenerator<void> {
    try {
      this.slackChannelsError = null;
      let params = {
        app_id: this.session.workspace.id,
        'includes[]': 'slack_integration',
        only_active_channels: 'true',
        channel_purpose: 'conversational',
      };
      let channels: SlackChannel[] = yield get('/ember/slack/v2/channels', params);
      this.slackChannels = channels;
    } catch (e: unknown) {
      this.slackChannelsError = 'inbox.side-conversations.slack-channels-error';
    }
  }

  get channelOptions(): ChannelOption[] {
    return this.slackChannels.map((channel) => ({
      text: channel.channel_name,
      value: channel.id,
      icon: channel.channel_type === 'private_channel' ? 'locked' : 'hash',
    }));
  }

  get groupedChannelOptions(): ChannelOption[] | GroupedChannelOption[] {
    let groupsMap = new Map<string, ChannelOption[]>();

    this.slackChannels.forEach((channel) => {
      let teamName = channel.slack_team_name;
      if (!groupsMap.has(teamName)) {
        groupsMap.set(teamName, []);
      }
      groupsMap.get(teamName)?.push({
        text: channel.channel_name,
        value: channel.id,
        icon: channel.channel_type === 'private_channel' ? 'locked' : 'hash',
      });
    });

    if (groupsMap.size === 1) {
      return this.channelOptions;
    }

    return Array.from(groupsMap.entries()).map(([teamName, channels]) => ({
      heading: teamName,
      items: channels,
    }));
  }

  get items(): ChannelOption[] | GroupedChannelOption[] {
    return this.hasMultipleWorkspaces ? this.groupedChannelOptions : this.channelOptions;
  }

  get hasMultipleWorkspaces(): boolean {
    let workspaces = new Set(this.slackChannels.map((c) => c.slack_team_name));
    return workspaces.size > 1;
  }

  get hasNoChannels(): boolean {
    return !this.isLoading && this.slackChannels.length === 0;
  }

  @action
  handleSelect(channelId: string | null) {
    this.args.onChannelChange(channelId);
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Inbox2::SideConversation::SlackChannelSelector': typeof SlackChannelSelector;
    'inbox2/side-conversation/slack-channel-selector': typeof SlackChannelSelector;
  }
}
